// app/utils/apiClient.js
// Centralized HTTP client for the JobEng mobile app.
// Replaces the 82+ inline `apiFetch` helpers scattered across screens.
//
// Public surface:
//   apiFetch(endpoint, options) - JSON requests
//   apiUpload(endpoint, formData) - multipart uploads
//   apiPost(endpoint, body) - shortcut for POST
//   apiPut(endpoint, body) - shortcut for PUT
//   apiDelete(endpoint) - shortcut for DELETE
//   getApiBase() - returns the base URL
//   setApiBase(url) - override base URL (useful for staging/local)
//   resolveUserId() - finds the numeric user ID across all known storage keys
//
// All functions return { success, data?, error?, status? } and never throw.

import AsyncStorage from '@react-native-async-storage/async-storage';

let API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

export const getApiBase = () => API_BASE;
export const setApiBase = (url) => { API_BASE = url; };
export const getApiKey = () => API_KEY;

/**
 * Resolves the active bearer token from AsyncStorage.
 * Checks all known keys in order of preference.
 */
export const resolveAuthToken = async () => {
  const candidates = await Promise.all([
    AsyncStorage.getItem('auth_token'),
    AsyncStorage.getItem('userToken'),
    AsyncStorage.getItem('token'),
  ]);
  return candidates.find(Boolean) || null;
};

/**
 * Resolves the user's numeric ID across all known storage keys.
 * Returns null if no valid numeric ID found.
 */
export const resolveUserId = async () => {
  const validNumericId = (v) => v != null && !isNaN(parseInt(v, 10)) && parseInt(v, 10) > 0;

  // Direct keys first (fastest)
  const directs = await Promise.all([
    AsyncStorage.getItem('user_id'),
    AsyncStorage.getItem('userId'),
    AsyncStorage.getItem('professional_user_id'),
    AsyncStorage.getItem('current_user_id'),
  ]);
  for (const v of directs) {
    if (validNumericId(v)) return parseInt(v, 10);
  }

  // JSON-wrapped fallbacks
  const jsons = await Promise.all([
    AsyncStorage.getItem('userData'),
    AsyncStorage.getItem('user'),
    AsyncStorage.getItem('auth_user'),
  ]);
  for (const raw of jsons) {
    if (!raw) continue;
    try {
      const obj = JSON.parse(raw);
      const candidate =
        obj?.id || obj?.user_id || obj?.userId ||
        obj?.user?.id || obj?.user?.user_id ||
        obj?.data?.user?.id || obj?.data?.id;
      if (validNumericId(candidate)) {
        const resolved = parseInt(candidate, 10);
        // Cache to canonical key for next time
        try { await AsyncStorage.setItem('user_id', String(resolved)); } catch {}
        return resolved;
      }
    } catch {}
  }
  return null;
};

/**
 * JSON fetch with auto auth headers.
 * @param {string} endpoint - path like '/api/data/users' (leading slash required)
 * @param {object} options - standard fetch options ({ method, body, headers, ... })
 * @returns {Promise<{success, data?, error?, status?}>}
 */
export const apiFetch = async (endpoint, options = {}) => {
  try {
    const token = await resolveAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    };
    const response = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
    if (!response.ok) {
      let errMsg = response.statusText || `HTTP ${response.status}`;
      try {
        const errJson = await response.json();
        errMsg = errJson.message || errJson.error || errMsg;
      } catch {}
      return { error: true, success: false, status: response.status, message: errMsg };
    }
    const data = await response.json().catch(() => ({}));
    // Backward-compat: many screens expect the data directly when success
    // (they pattern-match `if (!res.error && res.data)`). Return both shapes.
    return { success: true, error: false, status: response.status, data, ...data };
  } catch (err) {
    return { error: true, success: false, message: err.message };
  }
};

/**
 * Multipart upload — for file uploads.
 * Critical: do NOT set Content-Type; React Native sets the boundary automatically.
 */
export const apiUpload = async (endpoint, formData) => {
  try {
    const token = await resolveAuthToken();
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      body: formData,
      headers: {
        'x-api-key': API_KEY,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) {
      let errMsg = response.statusText || `HTTP ${response.status}`;
      try {
        const errJson = await response.json();
        errMsg = errJson.message || errJson.error || errMsg;
      } catch {}
      return { error: true, success: false, status: response.status, message: errMsg };
    }
    const data = await response.json().catch(() => ({}));
    return { success: true, error: false, status: response.status, data, ...data };
  } catch (err) {
    return { error: true, success: false, message: err.message };
  }
};

// Shortcuts
export const apiPost = (endpoint, body) =>
  apiFetch(endpoint, { method: 'POST', body: JSON.stringify(body) });
export const apiPut = (endpoint, body) =>
  apiFetch(endpoint, { method: 'PUT', body: JSON.stringify(body) });
export const apiPatch = (endpoint, body) =>
  apiFetch(endpoint, { method: 'PATCH', body: JSON.stringify(body) });
export const apiDelete = (endpoint) =>
  apiFetch(endpoint, { method: 'DELETE' });

// Default export for screens that prefer `import apiFetch from '../utils/apiClient'`
export default apiFetch;
